import chalk from "chalk";
import { spawnSync } from "node:child_process";
import { createInterface } from "node:readline";
import { mkdirSync, readdirSync, unlinkSync, existsSync } from "node:fs";
import { join } from "node:path";
import type { CaiConfig, SyncTarget, DriftIssue } from "../types.js";
import { runDriftCheck } from "../drift/index.js";
import { buildSyncBrief, buildCombinedBrief, type BriefFormat } from "./brief-builder.js";
import { generateRules } from "../rules.js";
import { writeIfChanged } from "../utils/fs.js";

function askUser(question: string): Promise<string> {
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

const CLAUDE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

function runClaudeInteractive(brief: string, cwd: string, model?: string): boolean {
  const args = model ? ["--model", model, brief] : [brief];
  const result = spawnSync("claude", args, {
    cwd,
    stdio: "inherit",
    timeout: CLAUDE_TIMEOUT_MS,
  });
  if (result.error) {
    const code = (result.error as NodeJS.ErrnoException).code;
    if (code === "ENOENT") {
      console.error(chalk.red("  claude CLI not found. Install it from https://claude.ai/code"));
    } else if (code === "ETIMEDOUT") {
      console.log(chalk.yellow(`  Claude session timed out after ${CLAUDE_TIMEOUT_MS / 60_000}min. Checking partial results...`));
      return true;
    } else {
      console.error(chalk.red(`  claude failed: ${result.error.message}`));
    }
    return false;
  }
  // 130 = Ctrl+C, 143 = SIGTERM — treat as non-fatal
  if (result.status === 130 || result.status === 143) return false;
  return result.status === 0 || result.status === null;
}

/**
 * Headless coding-agent registry. Each agent applies the scaffold edits
 * non-interactively (no TTY) and auto-approves tool use — safe here because sync
 * only ever touches `.cai/` docs, never source. The command/args are tuned per
 * CLI; any can be overridden with CAI_AGENT_CMD (a `{brief}` placeholder is
 * replaced with the prompt) so new or differently-flagged agents work without a
 * code change.
 */
type AgentSpec = { bin: string; args: (brief: string, model?: string) => string[]; install: string };
const AGENTS: Record<string, AgentSpec> = {
  claude: {
    bin: "claude",
    args: (brief, model) => [...(model ? ["--model", model] : []), "--dangerously-skip-permissions", "-p", brief],
    install: "https://claude.ai/code",
  },
  codex: {
    // OpenAI Codex CLI — `exec` is the non-interactive subcommand.
    bin: "codex",
    args: (brief, model) => ["exec", "--full-auto", ...(model ? ["--model", model] : []), brief],
    install: "https://github.com/openai/codex",
  },
  copilot: {
    // GitHub Copilot CLI (agentic) — headless prompt with tools allowed.
    bin: "copilot",
    args: (brief, model) => ["-p", brief, "--allow-all-tools", ...(model ? ["--model", model] : [])],
    install: "https://github.com/github/copilot-cli",
  },
};

/** Common install dirs so a Finder-launched GUI (minimal PATH) still finds the
 *  agent binary. Prepended to the inherited PATH for the spawn only. */
function augmentedPath(): string {
  const home = process.env.HOME ?? "";
  const extra = [
    `${home}/.local/bin`,
    "/usr/local/bin",
    "/opt/homebrew/bin",
    `${home}/.bun/bin`,
    `${home}/.npm-global/bin`,
    `${home}/.cargo/bin`,
    "/usr/bin",
    "/bin",
  ];
  return [...extra, process.env.PATH ?? ""].filter(Boolean).join(":");
}

/** Run the chosen agent headlessly. `agent` is a registry key (default claude),
 *  or CAI_AGENT_CMD overrides the whole command. Returns true on success. */
function runAgentHeadless(brief: string, cwd: string, agent = "claude", model?: string): boolean {
  const env = { ...process.env, PATH: augmentedPath() };
  const stdio: ["ignore", "inherit", "inherit"] = ["ignore", "inherit", "inherit"];

  // Full-command override (for agents not in the registry): `mytool --flag {brief}`.
  const override = process.env.CAI_AGENT_CMD;
  let bin: string;
  let args: string[];
  let install = "";
  if (override) {
    const parts = override.trim().split(/\s+/);
    bin = parts[0];
    args = parts.slice(1).map((p) => (p === "{brief}" ? brief : p));
    if (!parts.includes("{brief}")) args.push(brief);
  } else {
    const spec = AGENTS[agent] ?? AGENTS.claude;
    bin = spec.bin;
    args = spec.args(brief, model);
    install = spec.install;
  }

  const result = spawnSync(bin, args, { cwd, stdio, timeout: CLAUDE_TIMEOUT_MS, env });
  if (result.error) {
    const code = (result.error as NodeJS.ErrnoException).code;
    if (code === "ENOENT") {
      console.error(chalk.red(`  ${bin} CLI not found on PATH.${install ? ` Install it from ${install}` : ""}`));
    } else if (code === "ETIMEDOUT") {
      return true; // partial progress; caller re-checks drift
    } else {
      console.error(chalk.red(`  ${bin} failed: ${result.error.message}`));
    }
    return false;
  }
  if (result.status === 130 || result.status === 143) return false;
  return result.status === 0 || result.status === null;
}

/** Autonomous-mode cap so a headless run can't loop forever on stubborn drift. */
const MAX_HEADLESS_CYCLES = 3;

type SyncMode = "interactive" | "prompts";

/** Run targeted sync: detect → brief → AI → verify → ask → loop */
export async function runSync(
  config: CaiConfig,
  opts: { dryRun?: boolean; includeWarnings?: boolean; export?: boolean; format?: BriefFormat; model?: string; files?: string[]; headless?: boolean; agent?: string; initialReport?: import("../types.js").DriftReport }
): Promise<void> {
  let cycle = 0;
  let mode: SyncMode | null = null;

  while (true) {
    cycle++;

    // Step 1: Run drift check (skip on first cycle if caller already has a fresh report)
    if (cycle === 1) {
      console.log(chalk.bold("Running drift check..."));
      console.log(chalk.dim("  CAI updates your .cai/ scaffold docs to match your code — source files are never changed."));
    } else {
      console.log(chalk.bold("\nRe-checking for remaining drift..."));
    }

    const report = (cycle === 1 && opts.initialReport)
      ? opts.initialReport
      : await runDriftCheck(config, { incremental: cycle > 1 });

    if (cycle === 1) console.log();

    if (report.issues.length === 0) {
      console.log(chalk.green("  ✓ No drift detected — scaffold is in sync."));
      refreshRules(config);
      console.log();
      return;
    }

    const scoreColor = report.score >= 80 ? chalk.green : report.score >= 50 ? chalk.yellow : chalk.red;
    console.log(`  Drift score: ${scoreColor(`${report.score}/100`)}  ${chalk.dim(`(${report.issues.length} issues)`)}`);

    // Step 2: Group issues by file
    const relevantIssues = opts.includeWarnings
      ? report.issues
      : report.issues.filter((i) => {
          const fileHasError = report.issues.some(
            (other) => other.file === i.file && other.severity === "error"
          );
          return fileHasError;
        });

    if (relevantIssues.length === 0) {
      console.log();
      console.log(chalk.green("  ✓ No errors — only warnings remain."));
      console.log(chalk.dim("  Run cai sync --warnings to include them."));
      refreshRules(config);
      console.log();
      return;
    }

    let targets = groupIntoTargets(relevantIssues);

    // --file: restrict sync to specific scaffold files (used by the GUI sidecar).
    if (opts.files && opts.files.length > 0) {
      const wanted = new Set(opts.files);
      targets = targets.filter((t) => wanted.has(t.file));
      if (targets.length === 0) {
        console.log();
        console.log(chalk.green(`  ✓ No drift in the requested file${opts.files.length !== 1 ? "s" : ""}.`));
        console.log();
        return;
      }
    }

    console.log();
    console.log(chalk.dim("  ─── Files to fix ────────────────────────────────────"));
    for (const target of targets) {
      const errors = target.issues.filter((i) => i.severity === "error").length;
      const warnings = target.issues.filter((i) => i.severity === "warning").length;
      const parts = [errors > 0 ? chalk.red(`${errors} error${errors !== 1 ? "s" : ""}`) : "", warnings > 0 ? chalk.yellow(`${warnings} warning${warnings !== 1 ? "s" : ""}`) : ""].filter(Boolean).join(", ");
      console.log(`  ${chalk.cyan(target.file.padEnd(40))} ${parts}`);
    }
    console.log();

    // Dry run — show combined prompt and exit
    if (opts.dryRun) {
      console.log(
        chalk.dim("\n--dry-run: showing prompt without executing\n")
      );
      const brief = await buildCombinedBrief(targets, config.projectRoot, opts.format);
      console.log(brief);
      console.log();
      return;
    }

    // Export mode — write one prompt file per target into .cai/sync-queue/
    if (opts.export) {
      await exportSyncQueue(targets, config, opts.format);
      return;
    }

    // Headless (GUI): skip the interactive picker — always drive Claude directly.
    if (opts.headless) {
      mode = "interactive";
    }

    // Ask user for mode (only on first cycle)
    if (mode === null) {
      console.log(chalk.dim("  ─── How should we fix these? ────────────────────────"));
      console.log();
      console.log(`  ${chalk.cyan("1")}  ${chalk.bold("Interactive")}  ${chalk.dim("— Claude fixes everything, you watch (default)")}`);
      console.log(`  ${chalk.cyan("2")}  ${chalk.bold("Export")}       ${chalk.dim("— Write prompt files to .cai/sync-queue/ for manual paste")}`);
      console.log(`  ${chalk.cyan("3")}  ${chalk.bold("Exit")}         ${chalk.dim("— I'll handle it later")}`);
      console.log();
      console.log(chalk.dim("  Tip: Sync tasks are simple doc updates — a small/cheap model works fine."));
      console.log(chalk.dim("  Claude: --model haiku · Cursor/Copilot: select a smaller model in settings."));
      console.log();

      const choice = await askUser("  Choice [1-3] (default: 1): ");
      const picked = choice || "1";

      switch (picked) {
        case "1":
          mode = "interactive";
          break;
        case "2":
          await exportSyncQueue(targets, config, opts.format);
          return;
        case "3":
          console.log();
          console.log(chalk.dim("  No problem — run cai sync anytime to pick this up."));
          console.log();
          return;
        default:
          console.log();
          console.log(chalk.dim("  No problem — run cai sync anytime to pick this up."));
          console.log();
          return;
      }
    }

    // Step 3: Fix all files in one interactive session
    console.log();
    console.log(chalk.dim(`  Sending ${targets.length} file${targets.length !== 1 ? "s" : ""} to Claude — sit back while it fixes the drift...`));
    console.log();

    const brief = await buildCombinedBrief(targets, config.projectRoot, opts.format);
    const ok = opts.headless
      ? runAgentHeadless(brief, config.projectRoot, opts.agent, opts.model)
      : runClaudeInteractive(brief, config.projectRoot, opts.model);

    if (!ok) {
      console.log(chalk.red("  ✗ Claude session did not complete."));
      console.log(chalk.dim("  Run cai sync again to retry, or cai sync --export to export prompts."));
      console.log();
      return;
    }

    // Step 4: Verify
    const postReport = await runDriftCheck(config);
    const scoreDelta = postReport.score - report.score;
    const deltaStr = scoreDelta > 0 ? chalk.green(`+${scoreDelta}`) : scoreDelta === 0 ? chalk.yellow("+0") : chalk.red(`${scoreDelta}`);

    console.log();
    console.log(chalk.dim("  ─── Result ──────────────────────────────────────────"));
    console.log(`  Drift score: ${chalk.dim(`${report.score}`)} → ${chalk.bold(`${postReport.score}/100`)}  ${chalk.dim(`(${deltaStr})`)} `);
    console.log();

    // Step 5: Check if we should continue
    const remainingErrors = postReport.issues.filter((i) => i.severity === "error").length;
    const remainingWarnings = postReport.issues.filter((i) => i.severity === "warning").length;

    if (postReport.score === 100) {
      console.log(chalk.green("  ✓ Perfect score — everything is in sync."));
      console.log();
      return;
    }

    if (remainingErrors === 0 && !opts.includeWarnings) {
      if (remainingWarnings > 0) {
        console.log(chalk.green("  ✓ All errors resolved."));
        console.log(chalk.dim(`  ${remainingWarnings} warning${remainingWarnings !== 1 ? "s" : ""} remain — run cai sync --warnings to address them.`));
      }
      console.log();
      return;
    }

    const remaining = opts.includeWarnings ? remainingErrors + remainingWarnings : remainingErrors;

    // Headless: loop autonomously up to a cap, then stop (no prompts).
    if (opts.headless) {
      if (cycle >= MAX_HEADLESS_CYCLES) {
        console.log(chalk.dim(`  Stopped after ${MAX_HEADLESS_CYCLES} rounds — ${remaining} issue${remaining !== 1 ? "s" : ""} still remain.`));
        console.log();
        return;
      }
      console.log();
      continue;
    }

    const answer = await askUser(`  ${remaining} issue${remaining !== 1 ? "s" : ""} still remain. Run another round? [Y/n] `);

    if (answer.toLowerCase() === "n") {
      console.log();
      console.log(chalk.dim("  Paused. Run cai sync anytime to continue."));
      console.log();
      return;
    }
    console.log();
  }
}

async function exportSyncQueue(targets: SyncTarget[], config: CaiConfig, format: BriefFormat = "markdown"): Promise<void> {
  const queueDir = join(config.scaffoldRoot, "sync-queue");
  mkdirSync(queueDir, { recursive: true });

  // Clear stale exports from previous runs
  if (existsSync(queueDir)) {
    for (const f of readdirSync(queueDir)) {
      if (f.endsWith(".md") || f.endsWith(".xml")) unlinkSync(join(queueDir, f));
    }
  }

  const written: string[] = [];
  const ext = format === "xml" ? "xml" : "md";

  for (const target of targets) {
    const brief = await buildSyncBrief(target, config.projectRoot, format);
    // Turn "context/architecture.md" → "fix-context-architecture.md"
    const slug = target.file.replace(/\//g, "-").replace(/\.md$/, "");
    const filename = `fix-${slug}.${ext}`;
    const filePath = join(queueDir, filename);
    writeIfChanged(filePath, brief);
    written.push(filename);
  }

  const rel = (p: string) => p.replace(config.projectRoot + "/", "");

  console.log();
  console.log(chalk.bold(`  cai sync --export`));
  console.log();
  console.log(chalk.green(`  ✓ ${written.length} prompt file${written.length === 1 ? "" : "s"} written to ${rel(queueDir)}/`));
  console.log();
  for (const f of written) {
    console.log(chalk.cyan(`    ${rel(queueDir)}/${f}`));
  }
  console.log();
  console.log(chalk.dim("  Open each file, paste the contents into your AI tool,"));
  console.log(chalk.dim("  apply the changes, then run cai check to verify."));
  console.log();
}

/** Regenerate .claude/rules/ after scaffold changes */
function refreshRules(config: CaiConfig): void {
  try {
    const result = generateRules(config);
    if (result.written.length > 0) {
      console.log(chalk.dim(`  ✓ ${result.written.length} path-scoped rules refreshed`));
    }
  } catch {
    // Non-fatal
  }
}

function groupIntoTargets(issues: DriftIssue[]): SyncTarget[] {
  const byFile = new Map<string, DriftIssue[]>();
  for (const issue of issues) {
    if (!byFile.has(issue.file)) byFile.set(issue.file, []);
    byFile.get(issue.file)!.push(issue);
  }

  return Array.from(byFile.entries()).map(([file, issues]) => ({
    file,
    issues,
    gitDiff: null,
  }));
}
